// Enabling / disabling buckets on a collection (v0.4). Enabling a collection bound to Deck or
// To Do first explains what changes there.
import { useEffect, useState } from "react";
import type { Manifest } from "../api/types";
import { enableBucketsNotes, type BindingLike } from "../lib/bucketRemote";
import { loadConnectors, setBucketsEnabled, store, toast, toastError } from "../store/app";
import { useApp } from "../store/hooks";
import { Modal } from "./bits";

export interface BucketsTarget {
  id: string;
  name: string;
  buckets_enabled?: boolean;
  bindings: BindingLike[];
}

async function apply(c: BucketsTarget, enabled: boolean, onDone?: (enabled: boolean) => void): Promise<void> {
  try {
    await setBucketsEnabled(c.id, enabled);
    toast(
      enabled ? `Buckets enabled for “${c.name}”` : `Buckets disabled for “${c.name}” (kept; re-enable any time)`,
      "success",
    );
    onDone?.(enabled);
  } catch (e) {
    toastError(e, enabled ? "Could not enable buckets" : "Could not disable buckets");
    throw e;
  }
}

/**
 * Menu action "Enable buckets" / "Disable buckets": disabling and enabling without affected
 * bindings apply directly; otherwise `confirm()` is called to show the EnableBucketsDialog.
 */
export function toggleBuckets(
  c: BucketsTarget,
  confirm: () => void,
  onDone?: (enabled: boolean) => void,
  manifests: Manifest[] | null = store.get().connectors,
): void {
  if (!c.buckets_enabled && enableBucketsNotes(c.bindings, manifests).length) confirm();
  else void apply(c, !c.buckets_enabled, onDone).catch(() => undefined);
}

export function EnableBucketsDialog({
  collection,
  onClose,
  onEnabled,
}: {
  collection: BucketsTarget;
  onClose: () => void;
  onEnabled?: () => void;
}) {
  const manifests = useApp((s) => s.connectors);
  useEffect(() => {
    void loadConnectors();
  }, []);
  const notes = enableBucketsNotes(collection.bindings, manifests);
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={`Enable buckets for “${collection.name}”?`} onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            await apply(collection, true, () => onEnabled?.());
            onClose();
          } catch {
            setBusy(false);
          }
        }}
      >
        <p>Buckets are named columns, independent of status. This collection is synced, so:</p>
        <ul className="binding-effects">
          {notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
        <p className="muted small">Disabling later keeps the buckets stored but hides them.</p>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={busy} autoFocus>
            {busy ? "Enabling…" : "Enable buckets"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
